import React from 'react';
import { MessageSquare, Phone } from 'lucide-react';
import { Company, DEFAULT_COMPANY } from '../../utils/company';

/**
 * The shared skin for every screen a customer sees — the quote, the invoice,
 * their own page, the plans page and a referral link.
 *
 * #df00ff is the brand (owner, Sep 19 2026) — the mascot's own colour, not a
 * darker purple. It runs the page, and black, white and grey are the accents:
 *
 *   brand-50   the page itself, a tint of #df00ff rather than grey
 *   brand-600  #df00ff exactly: the header band, the big panels, every action
 *
 * Text on a #df00ff surface is BLACK (5.6:1), which is what lets real
 * sentences sit on the brand colour instead of only headlines. The one
 * exception is a button label, which stays white the way the website sets it,
 * at 19px/900 so it clears the contrast floor for large bold text.
 *
 * Corners are rounded, never cut. Depth comes from purple-tinted shadows, not
 * from grey ones. Everything tappable carries `press`, so a finger gets an
 * answer. Nothing a customer must read is under 14px or lighter than gray-600.
 */

/* ── the purple brand band ─────────────────────────────────────────────── */

export function PublicHeader({ company, context, sticky = false }: {
  company?: Company; context?: string; sticky?: boolean;
}) {
  const co = company || DEFAULT_COMPANY;
  return (
    <header className={`bg-brand-600 text-white shadow-brand ${sticky ? 'sticky top-0 z-30' : ''}`}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-3">
        <span className="flex-shrink-0 h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center">
          <img src="/logo-white.png" alt="" width={32} height={32} className="h-8 w-8 object-contain" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-black uppercase tracking-tight leading-none text-[14px] whitespace-nowrap">Keep Austin Mowed</p>
          {context && <p className="text-[13px] text-white/85 truncate mt-1">{context}</p>}
        </div>
        <a href={`sms:${co.phone_raw}`} aria-label={`Text ${co.phone}`}
          className="press flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-brand-700 px-3.5 h-11 rounded-2xl shadow-soft text-[11px] font-black uppercase tracking-wide">
          <MessageSquare className="h-4 w-4" aria-hidden /> Text us
        </a>
      </div>
    </header>
  );
}

/* ── page frame ────────────────────────────────────────────────────────── */

export function PublicPage({ company, context, sticky, children, footerNote }: {
  company?: Company; context?: string; sticky?: boolean; children: React.ReactNode; footerNote?: React.ReactNode;
}) {
  const co = company || DEFAULT_COMPANY;
  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <PublicHeader company={co} context={context} sticky={sticky} />
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-5 space-y-4">{children}</main>
      <PublicFooter company={co} note={footerNote} />
    </div>
  );
}

export function PublicFooter({ company, note }: { company?: Company; note?: React.ReactNode }) {
  const co = company || DEFAULT_COMPANY;
  return (
    <footer className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 text-center">
      {note && <div className="text-sm text-gray-700 mb-3">{note}</div>}
      <p className="text-sm text-gray-700">
        Questions? Text or call{' '}
        <a href={`tel:${co.phone_raw}`} className="font-bold text-brand-700 underline">{co.phone}</a>
      </p>
      <p className="text-sm text-gray-600 mt-1">{co.name} · Georgetown, TX</p>
    </footer>
  );
}

/* ── headings ──────────────────────────────────────────────────────────── */

export function H1({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <h1 className={`font-black uppercase tracking-tight text-gray-900 text-3xl leading-[1.05] ${className}`}>{children}</h1>;
}
export function H2({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <h2 className={`font-black uppercase tracking-tight text-gray-900 text-xl leading-tight ${className}`}>{children}</h2>;
}

/* ── cards ─────────────────────────────────────────────────────────────── */

export function Panel({ title, action, children, tone = 'plain', className = '' }: {
  title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode;
  tone?: 'plain' | 'brand' | 'hot' | 'good' | 'warn'; className?: string;
}) {
  const skin =
    tone === 'brand' ? 'bg-brand-100 border-brand-300 shadow-soft'
    : tone === 'hot' ? 'bg-brand-600 border-brand-600 shadow-brand text-black'
    : tone === 'good' ? 'bg-emerald-50 border-emerald-300 shadow-soft'
    : tone === 'warn' ? 'bg-amber-50 border-amber-300 shadow-soft'
    : 'bg-white border-brand-100 shadow-soft';
  const heading = 'text-gray-900';
  return (
    <section className={`rounded-3xl border p-5 ${skin} ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 mb-3">
          {title && <h2 className={`font-black uppercase tracking-tight text-base leading-tight ${heading}`}>{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/* ── buttons ───────────────────────────────────────────────────────────── */

type BtnProps = {
  children: React.ReactNode; onClick?: () => void; href?: string; disabled?: boolean;
  type?: 'button' | 'submit'; className?: string; block?: boolean; 'aria-label'?: string;
};

const BTN_BASE =
  'press press-lift inline-flex items-center justify-center gap-2 px-6 min-h-[56px] py-3 rounded-2xl ' +
  'text-[19px] leading-none font-black uppercase tracking-wide select-none focus:outline-none ' +
  'focus-visible:ring-4 focus-visible:ring-brand-300 disabled:opacity-50 disabled:pointer-events-none';

export function ButtonPrimary({ children, onClick, href, disabled, type = 'button', className = '', block = true, ...rest }: BtnProps) {
  const cls = `${BTN_BASE} bg-brand-600 text-white shadow-brand hover:bg-brand-500 active:shadow-soft ${block ? 'w-full' : ''} ${className}`;
  // White on #df00ff is 3.8:1, so the label is 19px/900 — large bold text,
  // which clears the 3:1 floor. Do not shrink it.
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button type={type} onClick={onClick} disabled={disabled} className={cls} {...rest}>{children}</button>;
}

export function ButtonGhost({ children, onClick, href, disabled, type = 'button', className = '', block = true, ...rest }: BtnProps) {
  const cls = `${BTN_BASE} bg-white text-brand-700 border-2 border-brand-300 shadow-soft hover:bg-brand-50 ${block ? 'w-full' : ''} ${className}`;
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button type={type} onClick={onClick} disabled={disabled} className={cls} {...rest}>{children}</button>;
}

/* ── a tappable option row (add-ons, choices, tips) ───────────────────── */

export function OptionRow({ on, onClick, children, ariaLabel }: {
  on: boolean; onClick: () => void; children: React.ReactNode; ariaLabel?: string;
}) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} aria-label={ariaLabel}
      className={`press w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-4 text-left min-h-[60px]
        ${on ? 'border-brand-600 bg-brand-100 shadow-soft' : 'border-brand-200 bg-white shadow-soft hover:border-brand-400'}`}>
      {children}
    </button>
  );
}

/* ── the sticky action bar ─────────────────────────────────────────────── */

/**
 * The price and the one button, always on screen. Before this, a customer on a
 * phone had to scroll past everything to find out what it cost and how to say
 * yes — the two things they opened the link for.
 */
export function StickyBar({ label, value, sub, children }: {
  label?: string; value?: React.ReactNode; sub?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-6 mt-4 border-t-2 border-brand-200 bg-white/95 backdrop-blur shadow-[0_-8px_24px_-8px_rgba(74,0,84,0.18)] px-4 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] rounded-t-3xl">
      {(label || value) && (
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <span className="text-sm font-bold text-gray-700">{label}</span>
          <span className="text-2xl font-black tracking-tight text-brand-600 tabular-nums text-right">{value}</span>
        </div>
      )}
      {children}
      {sub && <p className="text-[14px] font-semibold text-gray-800 text-center mt-2">{sub}</p>}
    </div>
  );
}

/* ── one "this link is dead" screen, worded once ───────────────────────── */

export function DeadLink({ what, company }: { what: string; company?: Company }) {
  const co = company || DEFAULT_COMPANY;
  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <PublicHeader company={co} />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="bg-white rounded-3xl border border-brand-100 shadow-lift p-6 max-w-sm w-full text-center">
          <H2>That link has expired</H2>
          <p className="text-[15px] text-gray-800 mt-3">
            No trouble at all. Text or call and we will send you a fresh {what} link right away.
          </p>
          <div className="mt-5 space-y-2">
            <ButtonPrimary href={`sms:${co.phone_raw}`}><MessageSquare className="h-5 w-5" aria-hidden /> Text {co.phone}</ButtonPrimary>
            <ButtonGhost href={`tel:${co.phone_raw}`}><Phone className="h-5 w-5" aria-hidden /> Call us</ButtonGhost>
          </div>
        </div>
      </div>
      <PublicFooter company={co} />
    </div>
  );
}

/* ── loading ───────────────────────────────────────────────────────────── */

export function PublicLoading() {
  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" role="status" aria-label="Loading" />
      </div>
    </div>
  );
}
